
import { defineComponent, ref, computed, watch } from 'vue';
import { ComponentName } from '@/products/api/configuration/components/ComponentName';
import { DataType } from '@/products/api/configuration/application/DataType';
import { setupComponent } from '@/products/composables/setupComponent';
import { IDetailsVariantsSelectorOptions } from '@/products/api/configuration/components/IDetailsVariantsSelectorOptions';
import { UnavailableValuesBehavior } from '@/products/api/configuration/components/UnavailableValuesBehavior';
import Clickable from '@/common/components/Clickable.vue';

import {
  ProductItem,
  ProductVariantAttribute,
  ProductVariantValue,
  VariantAttributeDisplayModeEnum,
} from '@/common/services/swagger/index.defs';

import InjectStyles from '@/common/components/InjectStyles.vue';
import Dropdown from '@/common/components/Dropdown.vue';
import { DetailsCommand } from '@/products/api/runtime/CommandExecutor';

export default defineComponent({
  name: ComponentName.detailsVariantsSelector,

  components: {
    InjectStyles,
    Dropdown,
    Clickable,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();

    const { componentName, isReady, isWebComponent, instance, store, t, routeData } =
      setupComponent(root, props.instanceId);

    const componentOptions = computed(
      () =>
        store.value?.options.components?.detailsVariantsSelector ??
        ({} as IDetailsVariantsSelectorOptions),
    );

    const variants = computed(() => {
      if (!store.value?.data.productVariantsSelector?.items?.length) return [];

      return store.value?.data.productVariantsSelector.items;
    });

    const visible = computed(() => {
      return componentOptions.value.visible !== false && (variants.value?.length ?? 0) > 0;
    });

    const getVariantValues = (attribute: ProductVariantAttribute) => {
      if (componentOptions.value.unavailableValuesBehavior === UnavailableValuesBehavior.Hidden) {
        return attribute.values?.filter((value) => value.match === true) || [];
      }
      return attribute.values || [];
    };

    const isDropdownOpen = ref({});

    const toggleDropdown = (attrCode: string) => {
      isDropdownOpen.value[attrCode] = !isDropdownOpen.value[attrCode];
    };

    const details = async (productVariantValue: ProductVariantValue) => {
      if (productVariantValue.product?.inactive) return;

      if (
        componentOptions.value.unavailableValuesBehavior === UnavailableValuesBehavior.Disabled &&
        !productVariantValue.match
      )
        return;

      if (routeData.value && productVariantValue.product) {
        try {
          await instance.value?.execute(new DetailsCommand(productVariantValue.product));
        } catch (error) {
          // Ignore Error
        }
      }
    };

    const getSelectedValue = (attribute: ProductVariantAttribute): ProductVariantValue | null => {
      return attribute.values?.find((value) => value.selected) || null;
    };

    return {
      root,
      componentName,
      isReady,
      isWebComponent,
      store,
      t,
      componentOptions,
      variants,
      visible,
      isDropdownOpen,
      UnavailableValuesBehavior,
      VariantAttributeDisplayModeEnum,
      getVariantValues,
      details,
      toggleDropdown,
      getSelectedValue,
      Clickable,
    };
  },
});
